import { Phase } from '../core/game/phases.js';
import {
  INITIAL_STATE,
  reduce,
  type LoopEvent,
  type LoopState,
} from '../core/game/mission-director.js';
import type { SaveData } from '../core/save/model.js';

/**
 * One screen is mounted at a time. Screens never navigate directly to each other - they dispatch a
 * LoopEvent and the pure reducer decides what happens next. That indirection is what makes the dev
 * overlay able to jump anywhere, and what makes the whole loop testable without a browser.
 */

export interface ScreenContext {
  /** Send an event to the state machine. Illegal events are silently ignored, by design. */
  readonly dispatch: (event: LoopEvent) => void;
  /** Snapshot at mount time. For changes within the same phase, use `subscribe`. */
  readonly state: LoopState;
  /**
   * The save as it was when this screen mounted. It does NOT update when you call `updateSave` - a
   * screen that writes and then reads this back gets its own stale copy, which is exactly how the
   * reward screen once managed to award points and then fail to notice she could afford a hat.
   * Use `currentSave()` after any write.
   */
  readonly save: SaveData;
  /** The live save, reflecting every `updateSave` so far. */
  readonly currentSave: () => SaveData;
  /** Persist a change to the save data (fire and forget; the router keeps the in-memory copy). */
  readonly updateSave: (mutate: (current: SaveData) => SaveData) => void;
  /**
   * Observe state changes that do not swap the screen - the drive screen uses this to notice
   * `collected` flipping so the HUD can swap the item icon for the Lookout icon without a remount.
   * Returns an unsubscribe function; the router does not call it for you, so do it in unmount().
   */
  readonly subscribe: (listener: (state: LoopState) => void) => () => void;
}

export interface Screen {
  mount(root: HTMLElement, ctx: ScreenContext): void;
  /** Tear down listeners, timers, tickers, Pixi apps. Called before the next screen mounts. */
  unmount?(): void;
}

export type ScreenFactory = () => Screen;

export class Router {
  private state: LoopState = INITIAL_STATE;
  private current: Screen | null = null;
  private currentPhase: Phase | null = null;
  private readonly listeners = new Set<(state: LoopState) => void>();

  constructor(
    private readonly root: HTMLElement,
    private readonly screens: Readonly<Record<Phase, ScreenFactory>>,
    private save: SaveData,
    private readonly persist: (data: SaveData) => void,
  ) {}

  start(): void {
    this.render();
  }

  getState(): LoopState {
    return this.state;
  }

  getSave(): SaveData {
    return this.save;
  }

  onChange(listener: (state: LoopState) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  dispatch = (event: LoopEvent): void => {
    const next = reduce(this.state, event);
    // reduce() returns the same object for an event the current phase does not handle. She will
    // press things at the wrong moment constantly; that must be a no-op, not a re-render.
    if (next === this.state) return;

    this.state = next;
    for (const listener of this.listeners) listener(next);
    this.render();
  };

  /**
   * Force a phase directly, bypassing the state machine. Dev overlay only - shipping code must go
   * through dispatch so the legal-transition guarantees mean something.
   */
  forcePhase(phase: Phase, patch: Partial<LoopState> = {}): void {
    this.state = { ...this.state, phase, ...patch };
    for (const listener of this.listeners) listener(this.state);
    this.render();
  }

  private updateSave = (mutate: (current: SaveData) => SaveData): void => {
    this.save = mutate(this.save);
    this.persist(this.save);
  };

  private render(): void {
    // Re-mounting on every dispatch would restart the drive screen when the HUD changes, so only a
    // genuine phase change swaps screens.
    if (this.currentPhase === this.state.phase && this.current !== null) return;

    this.current?.unmount?.();
    this.root.replaceChildren();

    const factory = this.screens[this.state.phase];
    const screen = factory();
    this.current = screen;
    this.currentPhase = this.state.phase;

    screen.mount(this.root, {
      dispatch: this.dispatch,
      state: this.state,
      save: this.save,
      currentSave: () => this.save,
      updateSave: this.updateSave,
      subscribe: (listener) => this.onChange(listener),
    });
  }
}
